import { onError } from '@apollo/client/link/error'

import { logger, LOGGER_LEVEL } from '@/network/common/logger'
import { cleanAuthenticationCookie } from '@/providers/authentication/authentication.helpers'

type ErrorLinkParams = {
  raiseNetworkError: () => void
  raiseInternalError: () => void
}

/**
 * @private only to be used in apolloClient.ts
 */
export const getErrorLink = ({ raiseInternalError, raiseNetworkError }: ErrorLinkParams) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    const uuid = operation.getContext().response?.url.split('?uuid=')[1] || ''
    const meta = { operationName: operation.operationName, uuid }
    const isFailedLoginAttempt =
      operation.operationName === 'Login' && graphQLErrors?.[0]?.extensions?.code === 'UNAUTHENTICATED'
    if (isFailedLoginAttempt) {
      logger({ level: LOGGER_LEVEL.INFO, message: 'Failed login attempt', meta: { ...meta, graphQLErrors } })
      return
    }

    let message = 'Apollo error'
    if (graphQLErrors) {
      message = graphQLErrors.map((error) => error.message).join(', ')
      logger({ level: LOGGER_LEVEL.ERROR, message, meta: { ...meta, graphQLErrors } })
    } else if (networkError) {
      message = 'Network error'
      logger({ level: LOGGER_LEVEL.ERROR, message, meta: { ...meta, networkError } })
    }

    // Handle network errors specifically
    // Warning: According to the Apollo documentation:
    // "An error is passed as a networkError if a link further down the chain called the error callback on the observable. In most cases, graphQLErrors is the errors field of the result from the last next call."
    // This mean that networkError can contain errors that are not really "network errors" but rather GraphQL errors.
    // We then filter out using the status code to at least ignore BAD USER INPUT errors
    // @ts-expect-error: Property statusCode does not exist on type Error | ServerParseError | ServerError // TODO: Fix properly & remove this?
    if (networkError && networkError?.statusCode !== 400) {
      // @ts-expect-error: Property statusCode does not exist on type Error | ServerParseError | ServerError // TODO: Fix properly & remove this?
      if (networkError?.statusCode === 500) {
        raiseInternalError()
      } else {
        raiseNetworkError()
      }
    }

    if (graphQLErrors)
      graphQLErrors?.forEach((error) => {
        const errorCode = error?.extensions?.code

        // Redirect to login page if the user is unauthenticated
        if (errorCode === 'UNAUTHENTICATED') {
          // TODO: get the current page url and redirect to it after re-login
          cleanAuthenticationCookie()
          location.href = '/logout'
          return
        }
      })
  })
